import { useMutation } from "@apollo/client";
import classNames from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import { LuCheck, LuPlus, LuX } from "react-icons/lu";

import { LinkConstants } from "../../app/constants/TextConstants";
import {
  OnboardOrg,
  OnboardOrgVariables,
} from "../../generated/operation-result-types";
import { ONBOARD_ORG_GQL } from "../../queries/OnboardingQueries.gql";
import {
  useAddUserMutation,
  useGetPermissionsQuery,
  useGetUserRoleEnumQuery,
} from "../../slices/UserSlice";
import { GetPageTitle } from "../../support/ScrollToTop";
import { AU_STATES, CountryCode } from "../region/region";
import {
  presetPermissions,
  visiblePermissions,
} from "../settings/team/editUserParts";
import { FlagIcon } from "../region/FlagIcon";
import { saveWelcome } from "./journey/journey.utils";
import {
  JOURNEY_BUTTON,
  JOURNEY_GHOST,
  JOURNEY_INPUT,
  JOURNEY_LABEL,
  JourneyError,
  JourneyLayout,
  JourneyTitle,
  StepBars,
} from "./journey/JourneyParts";
import {
  FirmForm,
  firmStepProblems,
  postalLabel,
  regionLabel,
  TEAM_LEVELS,
  TeamRow,
  teamRowProblem,
} from "./orgOnboarding.utils";

type Props = {
  orgId: string;
  userId: string;
  /** The firm name typed on the sign-up form (pre-filled). */
  defaultName?: string;
  onDone: () => void;
};

const provinces = [
  { value: "BC", label: "British Columbia", country: "CA" },
  { value: "NL", label: "Newfoundland", country: "CA" },
  { value: "PE", label: "Prince Edward Island", country: "CA" },
  { value: "NS", label: "Nova Scotia", country: "CA" },
  { value: "NB", label: "New Brunswick", country: "CA" },
  { value: "QC", label: "Quebec", country: "CA" },
  { value: "ON", label: "Ontario", country: "CA" },
  { value: "MB", label: "Manitoba", country: "CA" },
  { value: "SK", label: "Saskatchewan", country: "CA" },
  { value: "AB", label: "Alberta", country: "CA" },
  { value: "YT", label: "Yukon", country: "CA" },
  { value: "NT", label: "Northwest Territories", country: "CA" },
  { value: "NU", label: "Nunavut", country: "CA" },
  { value: "AL", label: "Alabama", country: "US" },
  { value: "AK", label: "Alaska", country: "US" },
  { value: "AS", label: "American Samoa", country: "US" },
  { value: "AZ", label: "Arizona", country: "US" },
  { value: "AR", label: "Arkansas", country: "US" },
  { value: "CA", label: "California", country: "US" },
  { value: "CO", label: "Colorado", country: "US" },
  { value: "CT", label: "Connecticut", country: "US" },
  { value: "DE", label: "Delaware", country: "US" },
  { value: "DC", label: "District Of Columbia", country: "US" },
  { value: "FL", label: "Florida", country: "US" },
  { value: "GA", label: "Georgia", country: "US" },
  { value: "GU", label: "Guam", country: "US" },
  { value: "HI", label: "Hawaii", country: "US" },
  { value: "ID", label: "Idaho", country: "US" },
  { value: "IL", label: "Illinois", country: "US" },
  { value: "IN", label: "Indiana", country: "US" },
  { value: "IA", label: "Iowa", country: "US" },
  { value: "KS", label: "Kansas", country: "US" },
  { value: "KY", label: "Kentucky", country: "US" },
  { value: "LA", label: "Louisiana", country: "US" },
  { value: "ME", label: "Maine", country: "US" },
  { value: "MD", label: "Maryland", country: "US" },
  { value: "MA", label: "Massachusetts", country: "US" },
  { value: "MI", label: "Michigan", country: "US" },
  { value: "MN", label: "Minnesota", country: "US" },
  { value: "MS", label: "Mississippi", country: "US" },
  { value: "MO", label: "Missouri", country: "US" },
  { value: "MT", label: "Montana", country: "US" },
  { value: "NE", label: "Nebraska", country: "US" },
  { value: "NV", label: "Nevada", country: "US" },
  { value: "NH", label: "New Hampshire", country: "US" },
  { value: "NJ", label: "New Jersey", country: "US" },
  { value: "NM", label: "New Mexico", country: "US" },
  { value: "NY", label: "New York", country: "US" },
  { value: "NC", label: "North Carolina", country: "US" },
  { value: "ND", label: "North Dakota", country: "US" },
  { value: "MP", label: "Northern Mariana IS", country: "US" },
  { value: "OH", label: "Ohio", country: "US" },
  { value: "OK", label: "Oklahoma", country: "US" },
  { value: "OR", label: "Oregon", country: "US" },
  { value: "PA", label: "Pennsylvania", country: "US" },
  { value: "PR", label: "Puerto Rico", country: "US" },
  { value: "RI", label: "Rhode Island", country: "US" },
  { value: "SC", label: "South Carolina", country: "US" },
  { value: "SD", label: "South Dakota", country: "US" },
  { value: "TN", label: "Tennessee", country: "US" },
  { value: "TX", label: "Texas", country: "US" },
  { value: "UT", label: "Utah", country: "US" },
  { value: "VT", label: "Vermont", country: "US" },
  { value: "VI", label: "Virgin Islands", country: "US" },
  { value: "VA", label: "Virginia", country: "US" },
  { value: "WA", label: "Washington", country: "US" },
  { value: "WV", label: "West Virginia", country: "US" },
  { value: "WI", label: "Wisconsin", country: "US" },
  { value: "WY", label: "Wyoming", country: "US" },
  ...AU_STATES.map((s) => ({ ...s, country: "AU" })),
];

const COUNTRIES: { code: CountryCode; flag: string; name: string }[] = [
  { code: "CA", flag: "🇨🇦", name: "Canada" },
  { code: "US", flag: "🇺🇸", name: "United States" },
  { code: "AU", flag: "🇦🇺", name: "Australia" },
  { code: "GB", flag: "🇬🇧", name: "United Kingdom" },
];

const emptyRow = (): TeamRow => ({
  firstName: "",
  lastName: "",
  email: "",
  level: "Staff (No Settings)",
});

type SetupLine = { text: string; state: "done" | "busy" | "todo" | "failed" };

/**
 * New firm setup after the first sign-in (redesigned 2026-10-25, owner): Step 2 of 3 "Your firm"
 * (name, country tiles, address), Step 3 of 3 "Your team (optional)" (invitations), then a short
 * "Setting up…" screen (onboardOrg: work types, statuses, free trial; then the invitations) and the
 * welcome window with confetti. Step 1 (password) is the /setup page from the welcome email.
 */
export const OrgOnboardingView: React.FC<Props> = ({
  orgId,
  userId,
  defaultName,
  onDone,
}) => {
  useEffect(() => {
    document.title = GetPageTitle("Set up your firm");
  }, []);

  const [step, setStep] = useState<"firm" | "team" | "setup">("firm");
  const [firm, setFirm] = useState<FirmForm>({
    name: (defaultName || "").trim(),
    // Nothing picked in advance (owner 2026-10-25: they have to pick a country).
    country: "",
    line1: "",
    line2: "",
    city: "",
    region: "",
    postalCode: "",
  });
  const [firmTried, setFirmTried] = useState(false);
  const [rows, setRows] = useState<TeamRow[]>([emptyRow()]);
  const [teamTried, setTeamTried] = useState(false);
  const [lines, setLines] = useState<SetupLine[]>([]);
  const [setupError, setSetupError] = useState("");
  const [inviteProblems, setInviteProblems] = useState<string[]>([]);
  const [finished, setFinished] = useState<{ invited: number } | null>(null);

  const [onboardOrg] = useMutation<OnboardOrg, OnboardOrgVariables>(
    ONBOARD_ORG_GQL,
  );
  const [addUser] = useAddUserMutation();
  const { data: userRoleEnum } = useGetUserRoleEnumQuery();
  const { data: allPermissions } = useGetPermissionsQuery();
  const permissionIds = useMemo(
    () => (visiblePermissions(allPermissions) ?? []).map((p) => p.id),
    [allPermissions],
  );

  const firmProblems = firmStepProblems(firm);
  const regions = provinces.filter((item) => item.country === firm.country);
  const filledRows = rows.filter(
    (row) => row.firstName.trim() || row.lastName.trim() || row.email.trim(),
  );
  const rowProblems = rows.map(teamRowProblem);
  const teamProblem = rowProblems.find(Boolean) || null;

  const setField =
    (key: keyof FirmForm) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setFirm((current) => ({ ...current, [key]: event.target.value }));

  const pickCountry = (code: FirmForm["country"]) =>
    setFirm((current) =>
      current.country === code
        ? current
        : { ...current, country: code, region: "" },
    );

  const setRow = (index: number, patch: Partial<TeamRow>) =>
    setRows((current) =>
      current.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );

  const run = async (invites: TeamRow[]) => {
    setStep("setup");
    setSetupError("");
    setInviteProblems([]);
    const base: SetupLine[] = [
      { text: "Creating your firm", state: "busy" },
      {
        text: "Adding standard work types (tax returns, bookkeeping, payroll…)",
        state: "todo",
      },
      { text: "Adding work statuses", state: "todo" },
      { text: "Starting your free trial", state: "todo" },
      ...(invites.length
        ? [
            {
              text: `Sending your team's invitation${
                invites.length === 1 ? "" : "s"
              }`,
              state: "todo" as const,
            },
          ]
        : []),
    ];
    setLines(base);
    // The server does the first four in one go; tick them off while it works.
    let shown = 0;
    const timer = window.setInterval(() => {
      shown = Math.min(shown + 1, 3);
      setLines((current) =>
        current.map((line, index) =>
          index < shown
            ? { ...line, state: "done" }
            : index === shown
            ? { ...line, state: "busy" }
            : line,
        ),
      );
    }, 900);
    try {
      await onboardOrg({
        variables: {
          onboardOrgInput: {
            orgId,
            userId,
            name: firm.name.trim(),
            subscribeFreePlan: true,
            locationAddressInput: {
              line1: firm.line1.trim(),
              line2: firm.line2.trim() || null,
              city: firm.city.trim(),
              region: firm.region.trim(),
              country: firm.country,
              postalCode: firm.postalCode.trim(),
            },
          },
        },
      });
    } catch {
      window.clearInterval(timer);
      setSetupError(
        "Your firm couldn't be set up. Please try again in a minute.",
      );
      setLines((current) =>
        current.map((line) =>
          line.state === "done" ? line : { ...line, state: "todo" },
        ),
      );
      return;
    }
    window.clearInterval(timer);
    setLines((current) =>
      current.map((line, index) =>
        index < 4 ? { ...line, state: "done" } : { ...line, state: "busy" },
      ),
    );

    let invited = 0;
    const problems: string[] = [];
    for (const row of invites) {
      const roleId =
        userRoleEnum?.find((item) => item.userRole === row.level)?.id || "";
      try {
        await addUser({
          orgId,
          userRoleEnumId: roleId,
          firstName: row.firstName.trim(),
          lastName: row.lastName.trim(),
          email: row.email.trim(),
          permissionIds:
            row.level === "Admin"
              ? []
              : presetPermissions(row.level, permissionIds) ?? [],
          assignedAllClients: true,
          assignedCustomerIds: [],
        }).unwrap();
        invited += 1;
      } catch (error) {
        problems.push(
          `${row.firstName.trim()} ${row.lastName.trim()}: ${
            typeof error === "string"
              ? error
              : "the invitation couldn't be sent"
          }`,
        );
      }
    }
    setLines((current) =>
      current.map((line, index) =>
        index < 4
          ? line
          : { ...line, state: problems.length ? "failed" : "done" },
      ),
    );
    setInviteProblems(problems);
    if (problems.length) {
      setFinished({ invited });
      return;
    }
    finish(invited);
  };

  const finish = (invited: number) => {
    saveWelcome({
      kind: "owner",
      firmName: firm.name.trim(),
      invited,
      at: Date.now(),
    });
    // Let the last tick show for a moment.
    window.setTimeout(onDone, 700);
  };

  const side = (title: string, text: string) => (
    <>
      <div
        className={"text-[30px] font-extrabold leading-tight lg:text-[36px]"}>
        {title}
      </div>
      <div className={"mt-3.5 max-w-[380px] text-[15px] opacity-90"}>
        {text}
      </div>
    </>
  );

  if (step === "setup") {
    return (
      <div
        className={
          "flex min-h-screen items-center justify-center bg-gradient-to-br from-[#5B3EF0] to-[#8F6BFF] p-4 font-Manrope"
        }>
        <div
          className={
            "w-[480px] max-w-full rounded-[20px] bg-white px-7 py-9 shadow-[0_30px_80px_rgba(0,0,0,0.25)] sm:px-11"
          }>
          <div className={"text-xl font-extrabold text-[#6B4EFF]"}>
            ProCharted
          </div>
          <h1
            className={"mb-1.5 mt-4 text-[26px] font-extrabold text-[#1F2433]"}>
            {setupError
              ? "Something went wrong"
              : `Setting up ${firm.name.trim()}…`}
          </h1>
          <div className={"mb-5 text-[#7A7F8C]"}>
            {setupError || "This only takes a few seconds."}
          </div>
          {lines.map((line) => (
            <div
              key={line.text}
              className={classNames(
                "my-3 flex items-center gap-3 text-[15px]",
                line.state === "busy" && "font-bold text-[#1F2433]",
                line.state === "todo" && "text-[#8A90A2]",
                line.state === "done" && "text-[#1F2433]",
                line.state === "failed" && "text-[#B42318]",
              )}>
              <span
                className={classNames(
                  "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs",
                  line.state === "done" && "bg-[#E3F6EC] text-[#1E9E5A]",
                  line.state === "busy" &&
                    "animate-spin border-[3px] border-[#6B4EFF] border-r-[#E6E8EE]",
                  line.state === "todo" && "bg-[#F1F2F5]",
                  line.state === "failed" && "bg-[#FDECEC] text-[#C0392B]",
                )}>
                {line.state === "done" ? (
                  <LuCheck size={14} />
                ) : line.state === "failed" ? (
                  <LuX size={14} />
                ) : null}
              </span>
              {line.text}
            </div>
          ))}
          {inviteProblems.length > 0 && (
            <div
              className={
                "mt-4 rounded-lg border border-[#F5D0D0] bg-[#FDF3F3] px-3 py-2 text-[13px] text-[#B42318]"
              }>
              {inviteProblems.map((problem) => (
                <div key={problem}>{problem}</div>
              ))}
              <div className={"mt-1 text-[#7A7F8C]"}>
                You can invite them later in Settings › Team.
              </div>
            </div>
          )}
          {setupError && (
            <div className={"mt-5 flex gap-2.5"}>
              <button
                type={"button"}
                className={JOURNEY_GHOST}
                onClick={() => setStep("firm")}>
                Back
              </button>
              <button
                type={"button"}
                className={JOURNEY_BUTTON}
                onClick={() => void run(filledRows)}>
                Try again
              </button>
            </div>
          )}
          {finished && !setupError && (
            <button
              type={"button"}
              className={`${JOURNEY_BUTTON} mt-5`}
              onClick={() => finish(finished.invited)}>
              Continue
            </button>
          )}
          {!setupError && !finished && (
            <div className={"mt-6 h-1.5 rounded bg-[#EEF0F4]"}>
              <div
                className={"h-1.5 rounded bg-[#6B4EFF] transition-all"}
                style={{
                  width: `${Math.max(
                    8,
                    (lines.filter((line) => line.state === "done").length /
                      Math.max(1, lines.length)) *
                      100,
                  )}%`,
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (step === "team") {
    return (
      <JourneyLayout
        side={side(
          "Better together.",
          "Invite your team now or later from Settings › Team. They'll each get their own welcome email.",
        )}>
        <StepBars total={3} done={3} />
        <JourneyTitle
          step={"Step 3 of 3 · Your team (optional)"}
          title={"Who works with you?"}
          text={"Add their name and email, and pick what they can see."}
        />
        <div className={"grid gap-3"}>
          {rows.map((row, index) => (
            <div
              key={index}
              className={"rounded-xl border border-[#EDEFF3] p-3"}>
              <div className={"grid grid-cols-2 gap-2"}>
                <input
                  aria-label={`Person ${index + 1} first name`}
                  placeholder={"First name"}
                  value={row.firstName}
                  maxLength={100}
                  onChange={(event) =>
                    setRow(index, { firstName: event.target.value })
                  }
                  className={JOURNEY_INPUT}
                />
                <input
                  aria-label={`Person ${index + 1} last name`}
                  placeholder={"Last name"}
                  value={row.lastName}
                  maxLength={100}
                  onChange={(event) =>
                    setRow(index, { lastName: event.target.value })
                  }
                  className={JOURNEY_INPUT}
                />
              </div>
              <div className={"mt-2 flex gap-2"}>
                <input
                  aria-label={`Person ${index + 1} email`}
                  type={"email"}
                  placeholder={"name@yourfirm.com"}
                  value={row.email}
                  maxLength={255}
                  onChange={(event) =>
                    setRow(index, { email: event.target.value })
                  }
                  className={classNames(JOURNEY_INPUT, "min-w-0 flex-1")}
                />
                <select
                  aria-label={`Person ${index + 1} access`}
                  value={row.level}
                  onChange={(event) =>
                    setRow(index, { level: event.target.value })
                  }
                  className={classNames(
                    JOURNEY_INPUT.replace("w-full", ""),
                    "w-[150px] shrink-0",
                  )}>
                  {TEAM_LEVELS.map((level) => (
                    <option key={level.level} value={level.level}>
                      {level.title}
                    </option>
                  ))}
                </select>
                {rows.length > 1 && (
                  <button
                    type={"button"}
                    aria-label={`Remove person ${index + 1}`}
                    onClick={() =>
                      setRows((current) =>
                        current.filter((_, i) => i !== index),
                      )
                    }
                    className={"px-1 text-[#8A90A2] hover:text-[#C0392B]"}>
                    <LuX size={16} />
                  </button>
                )}
              </div>
              {teamTried && rowProblems[index] && (
                <div
                  className={"mt-1.5 text-[12.5px] font-medium text-[#D92D20]"}>
                  {rowProblems[index]}
                </div>
              )}
            </div>
          ))}
        </div>
        {rows.length < 10 && (
          <button
            type={"button"}
            onClick={() => setRows((current) => [...current, emptyRow()])}
            className={
              "mt-3 flex items-center gap-1.5 text-sm font-bold text-[#6B4EFF]"
            }>
            <LuPlus size={15} /> Add another
          </button>
        )}
        <div className={"mt-6 flex gap-2.5"}>
          <button
            type={"button"}
            className={JOURNEY_GHOST}
            onClick={() => setStep("firm")}>
            Back
          </button>
          <button
            type={"button"}
            className={JOURNEY_BUTTON}
            onClick={() => {
              setTeamTried(true);
              if (teamProblem) return;
              void run(filledRows);
            }}>
            {filledRows.length
              ? `Send ${filledRows.length} invitation${
                  filledRows.length === 1 ? "" : "s"
                } and finish`
              : "Finish"}
          </button>
        </div>
        <button
          type={"button"}
          onClick={() => void run([])}
          className={
            "mx-auto mt-3.5 block text-[13.5px] font-semibold text-[#7A7F8C] hover:text-[#1F2433]"
          }>
          Skip for now
        </button>
      </JourneyLayout>
    );
  }

  const show = (key: keyof FirmForm) =>
    firmTried ? firmProblems[key] : undefined;
  const inputClass = (key: keyof FirmForm) =>
    classNames(JOURNEY_INPUT, show(key) && "border-[#E5484D]");

  return (
    <JourneyLayout
      side={side(
        "Tell us about your firm.",
        "We use your country for the currency you pay in, the right tax words (SIN or SSN, GST or sales tax) and your address on invoices.",
      )}>
      <StepBars total={3} done={2} />
      <JourneyTitle
        step={"Step 2 of 3 · Your firm"}
        title={firm.name.trim() || "Your firm"}
      />
      <label htmlFor={"onb-name"} className={JOURNEY_LABEL}>
        Firm name
      </label>
      <input
        id={"onb-name"}
        value={firm.name}
        maxLength={100}
        onChange={setField("name")}
        className={inputClass("name")}
      />
      <FieldNote text={show("name")} />

      <span className={classNames(JOURNEY_LABEL, "mt-3.5")}>Country</span>
      <div
        role={"radiogroup"}
        aria-label={"Country"}
        className={"grid grid-cols-2 gap-2 sm:grid-cols-4"}>
        {COUNTRIES.map((country) => (
          <button
            key={country.code}
            type={"button"}
            role={"radio"}
            aria-checked={firm.country === country.code}
            onClick={() => pickCountry(country.code)}
            className={classNames(
              "rounded-[10px] border px-1.5 py-2.5 text-center text-[13px] font-semibold",
              firm.country === country.code
                ? "border-[#6B4EFF] bg-[#F7F5FF] text-[#4B32D6] shadow-[inset_0_0_0_1px_#6B4EFF]"
                : "border-[#DCE2EA] text-[#1F2433]",
            )}>
            <span className={"inline-flex items-center justify-center gap-1.5"}>
              <FlagIcon code={country.code} /> {country.name}
            </span>
          </button>
        ))}
      </div>
      <FieldNote text={show("country")} />

      <label
        htmlFor={"onb-line1"}
        className={classNames(JOURNEY_LABEL, "mt-3.5")}>
        Address
      </label>
      <input
        id={"onb-line1"}
        autoComplete={"address-line1"}
        placeholder={"Street address"}
        value={firm.line1}
        maxLength={100}
        onChange={setField("line1")}
        className={inputClass("line1")}
      />
      <FieldNote text={show("line1")} />
      <input
        aria-label={"Address line 2"}
        autoComplete={"address-line2"}
        placeholder={"Suite, unit (optional)"}
        value={firm.line2}
        maxLength={100}
        onChange={setField("line2")}
        className={classNames(JOURNEY_INPUT, "mt-2")}
      />
      <div
        className={
          "mt-3.5 grid grid-cols-1 gap-2.5 sm:grid-cols-[1.3fr_1fr_1fr]"
        }>
        <div>
          <label htmlFor={"onb-city"} className={JOURNEY_LABEL}>
            City
          </label>
          <input
            id={"onb-city"}
            autoComplete={"address-level2"}
            value={firm.city}
            maxLength={100}
            onChange={setField("city")}
            className={inputClass("city")}
          />
          <FieldNote text={show("city")} />
        </div>
        <div>
          <label htmlFor={"onb-region"} className={JOURNEY_LABEL}>
            {regionLabel(firm.country)}
          </label>
          {regions.length ? (
            <select
              id={"onb-region"}
              autoComplete={"address-level1"}
              value={firm.region}
              onChange={setField("region")}
              className={inputClass("region")}>
              <option value={""}>Choose…</option>
              {regions.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              id={"onb-region"}
              autoComplete={"address-level1"}
              value={firm.region}
              maxLength={100}
              onChange={setField("region")}
              className={inputClass("region")}
            />
          )}
          <FieldNote text={show("region")} />
        </div>
        <div>
          <label htmlFor={"onb-postal"} className={JOURNEY_LABEL}>
            {postalLabel(firm.country)}
          </label>
          <input
            id={"onb-postal"}
            autoComplete={"postal-code"}
            value={firm.postalCode}
            maxLength={20}
            onChange={setField("postalCode")}
            className={inputClass("postalCode")}
          />
          <FieldNote text={show("postalCode")} />
        </div>
      </div>
      <div className={"mt-4 text-[12.5px] text-[#7A7F8C]"}>
        Continuing starts your free trial.{" "}
        <a
          href={LinkConstants.bankStatements.pricing}
          target={"_blank"}
          rel={"noreferrer"}
          className={"font-semibold text-[#6B4EFF]"}>
          View plan details
        </a>
      </div>
      <JourneyError
        text={
          firmTried && Object.keys(firmProblems).length
            ? "Check the highlighted boxes."
            : ""
        }
      />
      <button
        type={"button"}
        className={`${JOURNEY_BUTTON} mt-5`}
        onClick={() => {
          setFirmTried(true);
          if (Object.keys(firmProblems).length) return;
          setStep("team");
        }}>
        Continue
      </button>
    </JourneyLayout>
  );
};

const FieldNote = ({ text }: { text?: string }) =>
  text ? (
    <div className={"mt-1 text-[12.5px] font-medium text-[#D92D20]"}>
      {text}
    </div>
  ) : null;
